import type { ErrorCode, ErrorDetail, Locale, MessageParams } from '@sentra/contracts';
import { isApiErrorBody, toErrorBody } from '@sentra/contracts';

/**
 * What a failed request becomes on this side of the wire, and how it becomes a sentence.
 *
 * **Three distinct failures, three classes, because the operator's next move differs.** An
 * {@link ApiError} is the server refusing something it understood: the code says what, and the
 * screen can branch on it. A {@link TransportError} is the server not being reached at all, which
 * is a cable or a container rather than anything the operator did. A {@link ResponseShapeError} is
 * a 2xx whose body is not what the contract says — a proxy's error page, or a client and a server
 * that have drifted — and the only honest thing to do with one is refuse to treat it as data.
 * Collapsing the three into one `Error` is how "no matches found" ends up on screen when the real
 * answer was "the request never left the building".
 *
 * **A message is rendered from the CODE, in the operator's language, not from the server's prose.**
 * AD-35 puts the whole contract in the code and says the client localizes; that is what lets the
 * language follow the operator's own choice rather than whatever `Accept-Language` the request
 * happened to carry, and what lets either catalogue be rewritten without breaking a consumer.
 *
 * **The one exception is a message whose placeholders only the server can fill**, and it is handled
 * by looking rather than by guessing. `errors.auth.account_locked` interpolates `{{minutes}}`,
 * which is a number this browser does not have. `@sentra/contracts`'s `interpolate` deliberately
 * leaves an unfilled placeholder standing, so {@link failureMessage} renders from the code, checks
 * whether any `{{...}}` survived, and falls back to the message the server already rendered when
 * one did. An operator never sees braces, and the catalogue is still the source.
 */

/** What the client itself could not do, as opposed to what the server refused. */
export type ClientFailureKind = 'transport' | 'response_shape';

/** A refusal the server sent, in the AD-35 contract. */
export class ApiError extends Error {
  readonly code: ErrorCode;

  readonly status: number;

  readonly details: readonly ErrorDetail[];

  /** `Retry-After` in whole seconds on a 429 or a 503, or `null` when the answer carried none. */
  readonly retryAfterSeconds: number | null;

  /** `X-Correlation-Id`, which is what an operator quotes when they report a fault. */
  readonly correlationId: string | null;

  /** The sentence the server rendered. Used only to fill a placeholder this side cannot. */
  readonly serverMessage: string;

  constructor(init: {
    code: ErrorCode;
    status: number;
    serverMessage: string;
    details?: readonly ErrorDetail[];
    retryAfterSeconds?: number | null;
    correlationId?: string | null;
  }) {
    super(`${init.code} (${String(init.status)})`);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.serverMessage = init.serverMessage;
    this.details = init.details ?? [];
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
    this.correlationId = init.correlationId ?? null;
  }
}

/** The request never produced a contract-shaped answer. */
export class TransportError extends Error {
  readonly kind: ClientFailureKind = 'transport';

  constructor(reason: string, options?: { cause?: unknown }) {
    super(reason, options);
    this.name = 'TransportError';
  }
}

/** A success whose body is not the documented shape. Never treated as data. */
export class ResponseShapeError extends Error {
  readonly kind: ClientFailureKind = 'response_shape';

  /** The route that answered, so a report names something greppable. */
  readonly routeId: string;

  constructor(routeId: string) {
    super(`${routeId} answered a body that is not its documented shape`);
    this.name = 'ResponseShapeError';
    this.routeId = routeId;
  }
}

/** Whether a thrown value is a refusal the server described. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** Whether a thrown value is a refusal with one particular code. */
export function hasCode(value: unknown, code: ErrorCode): boolean {
  return isApiError(value) && value.code === code;
}

/**
 * The two refusals that belong to the SESSION rather than to the work that was refused.
 *
 * **A screen never renders one of these as its own.** Both are raised by `auth/authorize.ts` before
 * a handler runs, so nothing was read, nothing was written and no idempotency key was consumed: the
 * only thing that happened is that this terminal is no longer usable until somebody signs back into
 * it. `SessionProvider` sees both through the client's `onFailure` hook and raises the overlay, and
 * that overlay is the whole of the operator's next move.
 *
 * The reason this is a predicate rather than a comparison at each call site is that the refusal
 * OUTLIVES the condition it describes. The refusal is cached — by TanStack on a query, by the
 * mutation observer on a write, by a `useState` on the screens that keep their own — and nothing
 * used to clear it when the terminal was unlocked, so "Locked after inactivity. Enter your PIN to
 * continue." stayed on screen, in a panel, on a terminal whose PIN had just been accepted. Every
 * surface that can hold a refusal asks this, and none of them holds these two.
 *
 * **It answers `boolean` rather than narrowing to {@link ApiError}, deliberately.** Its callers have
 * already established that much or do not care, and a predicate here would narrow the OTHER branch
 * to `never` inside {@link failureMessage} — where the value is an `ApiError` by that point — and
 * take the rest of that function's own narrowing down with it.
 */
export function isSessionGateRefusal(value: unknown): boolean {
  return (
    isApiError(value) &&
    (value.code === 'AUTH_SESSION_IDLE_LOCKED' || value.code === 'AUTH_SESSION_EXPIRED')
  );
}

/**
 * Builds an {@link ApiError} from a parsed response body, or `null` when the body is not the
 * contract.
 *
 * The distinction is the point: a reverse proxy answering 502 with an HTML page is not a refusal
 * this application can explain, and reading `body.error.code` off it would produce `undefined` in a
 * message box. `isApiErrorBody` is the contracts package's own recogniser, written for exactly this
 * caller.
 */
export function apiErrorFrom(
  body: unknown,
  meta: { status: number; retryAfterSeconds: number | null; correlationId: string | null },
): ApiError | null {
  if (!isApiErrorBody(body)) {
    return null;
  }
  return new ApiError({
    code: body.error.code,
    status: meta.status,
    serverMessage: body.error.message,
    ...(body.error.details === undefined ? {} : { details: body.error.details }),
    retryAfterSeconds: meta.retryAfterSeconds,
    correlationId: meta.correlationId,
  });
}

/**
 * The interpolation syntax the catalogues use, and therefore what an unfilled value looks like.
 *
 * Exported so there is one definition of "a message this side could not finish": `failureMessage`
 * uses it to fall back to the server's own rendering, and `api/details.ts` uses it as the net under
 * every per-field line. Two spellings of it is how one of them stops matching.
 */
export const UNFILLED_PLACEHOLDER = /\{\{\s*\w+\s*\}\}/u;

/**
 * The sentences this application owns, which are the ones the wire has no code for.
 *
 * Passed in rather than imported so this module stays free of React and of i18next: the same
 * function is called from a hook, from a test with a fixed catalogue, and from anywhere else that
 * has to turn a thrown value into text.
 */
export interface ClientMessages {
  readonly network: string;
  readonly unexpected: string;
  readonly responseInvalid: string;
  /**
   * What a refusal by the idle lock or an expired session is called on a screen.
   *
   * The catalogue's own sentence for those two codes tells the reader to enter their PIN, which is
   * the right thing to say to an API caller and the wrong thing to leave in a panel on a terminal
   * that has just been unlocked. This one is true in both states: the terminal locked, so whatever
   * was in flight did not finish. See {@link isSessionGateRefusal}.
   */
  readonly sessionInterrupted: string;
  /** The hint appended when the server said when to come back and its own message did not. */
  readonly retryAfter: (seconds: number) => string;
}

/**
 * One thrown value, as a sentence in the operator's language.
 *
 * `params` is what the browser knows and the code's message may want: today that is
 * `retryAfterSeconds`, which arrives in a header rather than in the body. Anything the browser does
 * not know is filled by falling back to the server's own rendering, which is the only place those
 * numbers exist.
 */
export function failureMessage(error: unknown, locale: Locale, messages: ClientMessages): string {
  if (error instanceof TransportError) {
    return messages.network;
  }
  if (error instanceof ResponseShapeError) {
    return messages.responseInvalid;
  }
  if (!isApiError(error)) {
    return messages.unexpected;
  }
  // **The session layer owns these two, so no screen renders the catalogue's sentence for them.**
  // Rendering "enter your PIN" beside a form is correct only while the overlay is up — and the
  // overlay is already saying it. A moment later the PIN has been accepted and the same words are
  // simply false. This one stays true either way, and it says the thing the operator has to act on:
  // nothing finished, so do it again.
  if (isSessionGateRefusal(error)) {
    return messages.sessionInterrupted;
  }

  const params: MessageParams =
    error.retryAfterSeconds === null ? {} : { retryAfterSeconds: error.retryAfterSeconds };
  const bare = toErrorBody({ code: error.code }, locale).error.message;
  const rendered = toErrorBody({ code: error.code, params }, locale).error.message;
  if (UNFILLED_PLACEHOLDER.test(rendered)) {
    // The catalogue wanted a value only the server has. Its own rendering already carries it.
    return error.serverMessage;
  }
  // `Retry-After` is a header, so it reaches the browser whatever the code says. When the code's
  // own message already asks for it — `SYSTEM_RATE_LIMITED` does — filling the placeholder is the
  // whole of the hint. When it does not, the number would otherwise be thrown away, and an
  // operator told only "try again later" retries immediately and is refused again.
  const alreadySaid = UNFILLED_PLACEHOLDER.test(bare);
  return error.retryAfterSeconds !== null && !alreadySaid
    ? `${rendered} ${messages.retryAfter(error.retryAfterSeconds)}`
    : rendered;
}
